import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Label, TextInput, Select, Badge, Spinner } from 'flowbite-react';
import { HiPlus, HiPencil, HiTrash, HiEye } from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Note: Organization endpoints pending backend implementation in template-core.yaml
// Uses API_ENDPOINTS.ORGANIZATIONS from centralized endpoints config

interface Organization {
  id: string;
  client_id: string;
  name: string;
  tier: 'OSS' | 'PRO' | 'ENTERPRISE';
  department?: string;
  location?: string;
  timezone?: string;
  client_name?: string;
  team_count?: number;
  user_count?: number;
  created_at: string;
  updated_at: string;
}

interface OrganizationDetails extends Organization {
  teams?: Array<{
    id: string;
    name: string;
    created_at: string;
  }>;
  users?: Array<{
    id: string;
    email: string;
    name?: string;
    user_role: string;
    org_role: string;
    granted_at: string;
    expires_at?: string;
  }>;
}

interface Client {
  id: string;
  name: string;
  tier: string;
  status: string;
}

const getTierBadgeColor = (tier: string): string => {
  switch (tier) {
    case 'OSS':
      return 'info';
    case 'PRO':
      return 'success';
    case 'ENTERPRISE':
      return 'purple';
    default:
      return 'gray';
  }
};

const OrganizationsPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [organizationDetails, setOrganizationDetails] = useState<OrganizationDetails | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    tier: 'PRO' as 'OSS' | 'PRO' | 'ENTERPRISE',
    department: '',
    location: '',
    timezone: 'UTC',
  });

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch organizations when client is selected
  useEffect(() => {
    if (selectedClient) {
      fetchOrganizations();
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await Make_Authorized_API_Call<Client>(
        API_ENDPOINTS.CLIENTS.LIST,
        'GET',
        undefined,
        { params: { limit: 100 } }
      );

      if (isSuccessResponse(response)) {
        setClients(response.data.Records);
        // Auto-select first client if available
        if (response.data.Records.length > 0 && !selectedClient) {
          setSelectedClient(response.data.Records[0].id);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch clients');
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients');
    }
  };

  const fetchOrganizations = async () => {
    if (!selectedClient) return;

    setLoading(true);
    setError(null);

    try {
      const response = await Make_Authorized_API_Call<Organization>(
        API_ENDPOINTS.ORGANIZATIONS.LIST,
        'GET',
        undefined,
        { params: { Client_ID: selectedClient, limit: 100 } }
      );

      if (isSuccessResponse(response)) {
        setOrganizations(response.data.Records);
      } else {
        throw new Error(response.message || 'Failed to fetch organizations');
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationDetails = async (orgId: string) => {
    try {
      const response = await Make_Authorized_API_Call<OrganizationDetails>(
        API_ENDPOINTS.ORGANIZATIONS.LIST,
        'GET',
        undefined,
        { params: { Organization_ID: orgId } }
      );

      if (isSuccessResponse(response) && response.data.Records.length > 0) {
        setOrganizationDetails(response.data.Records[0]);
        setShowDetailsModal(true);
      } else {
        throw new Error('Failed to fetch organization details');
      }
    } catch (err) {
      console.error('Error fetching organization details:', err);
      setError('Failed to load organization details');
    }
  };

  const handleCreate = async () => {
    if (!selectedClient) {
      setError('Please select a client first');
      return;
    }

    try {
      const response = await Make_Authorized_API_Call<Organization>(
        API_ENDPOINTS.ORGANIZATIONS.LIST,
        'POST',
        {
          Client_ID: selectedClient,
          ...formData,
        }
      );

      if (!isSuccessResponse(response)) {
        if (response.error_code === 'ENTITLEMENT_LIMIT_REACHED') {
          setError(response.message);
          return;
        }
        throw new Error(response.message || 'Failed to create organization');
      }

      setShowCreateModal(false);
      resetForm();
      fetchOrganizations();
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    }
  };

  const handleEdit = async () => {
    if (!selectedOrganization) return;

    try {
      const response = await Make_Authorized_API_Call<Organization>(
        API_ENDPOINTS.ORGANIZATIONS.LIST,
        'PUT',
        {
          Organization_ID: selectedOrganization.id,
          ...formData,
        }
      );

      if (isSuccessResponse(response)) {
        setShowEditModal(false);
        resetForm();
        fetchOrganizations();
      } else {
        throw new Error(response.message || 'Failed to update organization');
      }
    } catch (err) {
      console.error('Error updating organization:', err);
      setError('Failed to update organization');
    }
  };

  const handleDelete = async () => {
    if (!selectedOrganization) return;

    try {
      const response = await Make_Authorized_API_Call<Organization>(
        API_ENDPOINTS.ORGANIZATIONS.LIST,
        'DELETE',
        undefined,
        { params: { Organization_ID: selectedOrganization.id } }
      );

      if (isSuccessResponse(response)) {
        setShowDeleteModal(false);
        setSelectedOrganization(null);
        fetchOrganizations();
      } else {
        throw new Error(response.message || 'Failed to delete organization');
      }
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError('Failed to delete organization');
    }
  };

  const openCreateModal = () => {
    if (!selectedClient) {
      setError('Please select a client first');
      return;
    }
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (org: Organization) => {
    setSelectedOrganization(org);
    setFormData({
      name: org.name,
      tier: org.tier,
      department: org.department || '',
      location: org.location || '',
      timezone: org.timezone || 'UTC',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (org: Organization) => {
    setSelectedOrganization(org);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      tier: 'PRO',
      department: '',
      location: '',
      timezone: 'UTC',
    });
    setError(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Organizations
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage client organizations, departments, and teams
        </p>
      </div>

      {/* Client Selector */}
      <div className="mb-6 flex items-center gap-4">
        <div className="w-96">
          <Label htmlFor="clientSelect">Select Client</Label>
          <Select
            id="clientSelect"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            required
          >
            <option value="">Choose a client...</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name} ({client.tier})
              </option>
            ))}
          </Select>
        </div>
        <Button
          color="blue"
          onClick={openCreateModal}
          disabled={!selectedClient}
          className="mt-6"
        >
          <HiPlus className="mr-2 h-5 w-5" />
          Create Organization
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner size="xl" />
        </div>
      ) : !selectedClient ? (
        <div className="text-center text-gray-500 py-12">
          Please select a client to view organizations
        </div>
      ) : organizations.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          No organizations found for this client
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Tier</Table.HeadCell>
              <Table.HeadCell>Department</Table.HeadCell>
              <Table.HeadCell>Location</Table.HeadCell>
              <Table.HeadCell>Teams</Table.HeadCell>
              <Table.HeadCell>Users</Table.HeadCell>
              <Table.HeadCell>Created</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {organizations.map((org) => (
                <Table.Row key={org.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <Table.Cell className="font-medium text-gray-900 dark:text-white">
                    {org.name}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={getTierBadgeColor(org.tier)}>
                      {org.tier}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{org.department || '-'}</Table.Cell>
                  <Table.Cell>{org.location || '-'}</Table.Cell>
                  <Table.Cell>
                    <Badge color="gray">{org.team_count || 0}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color="gray">{org.user_count || 0}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(org.created_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-2">
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => fetchOrganizationDetails(org.id)}
                      >
                        <HiEye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openEditModal(org)}
                      >
                        <HiPencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => openDeleteModal(org)}
                      >
                        <HiTrash className="h-4 w-4" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Create Modal */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <Modal.Header>Create Organization</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Organization Name *</Label>
              <TextInput
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter organization name"
                required
              />
            </div>
            <div>
              <Label htmlFor="tier">Tier *</Label>
              <Select
                id="tier"
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value as 'OSS' | 'PRO' | 'ENTERPRISE' })}
                required
              >
                <option value="OSS">OSS</option>
                <option value="PRO">PRO</option>
                <option value="ENTERPRISE">ENTERPRISE</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <TextInput
                id="department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="Enter department"
              />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <TextInput
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Enter location"
              />
            </div>
            <div>
              <Label htmlFor="timezone">Timezone</Label>
              <TextInput
                id="timezone"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                placeholder="Enter timezone (e.g., UTC, America/New_York)"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCreate} disabled={!formData.name}>
            Create
          </Button>
          <Button color="gray" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)}>
        <Modal.Header>Edit Organization</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editName">Organization Name *</Label>
              <TextInput
                id="editName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="editTier">Tier *</Label>
              <Select
                id="editTier"
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value as 'OSS' | 'PRO' | 'ENTERPRISE' })}
                required
              >
                <option value="OSS">OSS</option>
                <option value="PRO">PRO</option>
                <option value="ENTERPRISE">ENTERPRISE</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="editDepartment">Department</Label>
              <TextInput
                id="editDepartment"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="editLocation">Location</Label>
              <TextInput
                id="editLocation"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="editTimezone">Timezone</Label>
              <TextInput
                id="editTimezone"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleEdit} disabled={!formData.name}>
            Save Changes
          </Button>
          <Button color="gray" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        size="3xl"
      >
        <Modal.Header>Organization Details</Modal.Header>
        <Modal.Body>
          {organizationDetails && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {organizationDetails.name}
                  </p>
                </div>
                <div>
                  <Label>Tier</Label>
                  <Badge color={getTierBadgeColor(organizationDetails.tier)}>
                    {organizationDetails.tier}
                  </Badge>
                </div>
                <div>
                  <Label>Client</Label>
                  <p className="text-gray-900 dark:text-white">
                    {organizationDetails.client_name}
                  </p>
                </div>
                <div>
                  <Label>Department</Label>
                  <p className="text-gray-900 dark:text-white">
                    {organizationDetails.department || '-'}
                  </p>
                </div>
                <div>
                  <Label>Location</Label>
                  <p className="text-gray-900 dark:text-white">
                    {organizationDetails.location || '-'}
                  </p>
                </div>
                <div>
                  <Label>Timezone</Label>
                  <p className="text-gray-900 dark:text-white">
                    {organizationDetails.timezone || '-'}
                  </p>
                </div>
              </div>

              <div>
                <Label>Teams</Label>
                {organizationDetails.teams && organizationDetails.teams.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {organizationDetails.teams.map((team) => (
                      <div
                        key={team.id}
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <p className="font-medium text-gray-900 dark:text-white">
                          {team.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Created: {new Date(team.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 mt-2">
                    No teams in this organization
                  </p>
                )}
              </div>

              <div>
                <Label>Users with Access</Label>
                {organizationDetails.users && organizationDetails.users.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {organizationDetails.users.map((user) => (
                      <div
                        key={user.id}
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {user.name || user.email}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Badge color="blue">{user.org_role}</Badge>
                            <Badge color="gray">{user.user_role}</Badge>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Granted: {new Date(user.granted_at).toLocaleDateString()}
                          {user.expires_at && ` • Expires: ${new Date(user.expires_at).toLocaleDateString()}`}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 mt-2">
                    No users have access to this organization
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label>Created</Label>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(organizationDetails.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label>Last Updated</Label>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(organizationDetails.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <Modal.Header>Delete Organization</Modal.Header>
        <Modal.Body>
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete <strong>{selectedOrganization?.name}</strong>?
          </p>
          <p className="text-sm text-red-600 dark:text-red-400 mt-2">
            This will also delete all teams and remove all user access grants for this organization.
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleDelete}>
            Delete
          </Button>
          <Button color="gray" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrganizationsPage;
